import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Quote, ArrowRight, Phone } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import { useSiteSettings } from "@/contexts/SiteSettingsContext";

// ─── SHARED TESTIMONIALS ─── Synced with admin panel
export function SharedTestimonials() {
  const { getPublishedPageContent } = useTheme();
  const content = getPublishedPageContent('home', 'testimonials');
  const c = {
    heading: "What People Say",
    subheading: "Testimonials",
    description: "Hear from our satisfied clients.",
    ...content,
  };
  const [idx, setIdx] = useState(0);

  // Read testimonials from admin localStorage
  const testimonials = useMemo(() => {
    try {
      const stored = localStorage.getItem('admin_testimonials_v1');
      if (stored) {
        const all = JSON.parse(stored);
        const active = all.filter((t: any) => t.is_active);
        if (active.length > 0) return active.map((t: any) => ({
          name: t.client_name,
          role: t.client_title,
          text: t.testimonial_text,
          image: t.client_image,
          rating: t.rating,
        }));
      }
    } catch {}
    return [
      { name: "Vikram Mehra", role: "Client", text: "Exceptional service and expertise. The team made the entire process seamless. Highly recommended!" },
      { name: "Anjali Kapoor", role: "Client", text: "Professional handling of our requirements. Exceeded our expectations with their transparent approach." },
      { name: "Suresh Bansal", role: "Client", text: "Outstanding service. They got us the best deal and the after-service support was exceptional." },
    ];
  }, []);

  const safeIdx = idx % testimonials.length;

  return (
    <section className="py-20 bg-primary text-primary-foreground overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
          {c.subheading && <span className="text-accent uppercase tracking-widest text-sm font-semibold block mb-4">{c.subheading}</span>}
          <h2 className="font-serif text-3xl md:text-4xl font-semibold">{c.heading}</h2>
          {c.description && <p className="text-primary-foreground/60 mt-4 max-w-xl mx-auto">{c.description}</p>}
        </motion.div>
        <div className="relative max-w-3xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div key={safeIdx} initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -50 }} className="text-center px-8">
              <Quote className="w-10 h-10 text-accent mx-auto mb-8 opacity-50" />
              <p className="text-xl md:text-2xl leading-relaxed mb-8 font-light">"{testimonials[safeIdx].text}"</p>
              <h4 className="font-serif text-lg font-semibold">{testimonials[safeIdx].name}</h4>
              <p className="text-primary-foreground/60 text-sm">{testimonials[safeIdx].role}</p>
            </motion.div>
          </AnimatePresence>
          <div className="flex items-center justify-center gap-4 mt-10">
            <button onClick={() => setIdx((p) => (p - 1 + testimonials.length) % testimonials.length)}
              className="w-10 h-10 rounded-full border border-primary-foreground/20 flex items-center justify-center hover:bg-accent hover:border-accent transition-all">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex gap-2">{testimonials.map((_: any, i: number) => (
              <button key={i} onClick={() => setIdx(i)} className={`w-2 h-2 rounded-full transition-all ${i === safeIdx ? 'w-8 bg-accent' : 'bg-primary-foreground/30'}`} />
            ))}</div>
            <button onClick={() => setIdx((p) => (p + 1) % testimonials.length)}
              className="w-10 h-10 rounded-full border border-primary-foreground/20 flex items-center justify-center hover:bg-accent hover:border-accent transition-all">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── SHARED CTA ─── With editable phone number
export function SharedCTA() {
  const { getPublishedPageContent } = useTheme();
  const { settings } = useSiteSettings();
  const content = getPublishedPageContent('home', 'cta');
  const settingsPhone = settings.contact.phone;
  const c = {
    heading: "Ready to Get Started?",
    description: "Connect with our team for a personalized consultation.",
    buttonText: "Get Started",
    buttonLink: "/contact",
    buttonText2: "Call Now",
    phoneNumber: settingsPhone,
    ...content,
    // Always override phoneNumber from settings (single source of truth)
    ...(content?.phoneNumber ? {} : { phoneNumber: settingsPhone }),
  };

  const formattedPhone = c.phoneNumber ? c.phoneNumber.replace(/(\+91)(\d{5})(\d{5})/, '$1 $2 $3') : '';

  return (
    <section className="relative py-20 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-accent to-accent/80" />
      <div className="max-w-[1400px] mx-auto px-6 relative z-10 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <h2 className="font-serif text-3xl md:text-4xl font-semibold text-accent-foreground mb-6">{c.heading}</h2>
          <p className="text-accent-foreground/80 text-lg max-w-xl mx-auto mb-10">{c.description}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={c.buttonLink || "/contact"} className="inline-flex items-center justify-center gap-2 px-10 py-4 bg-primary text-primary-foreground font-semibold text-sm uppercase tracking-wider hover:bg-primary/90 transition-all">
              {c.buttonText} <ArrowRight className="w-4 h-4" />
            </Link>
            {c.phoneNumber && (
              <a href={`tel:${c.phoneNumber}`} className="inline-flex items-center justify-center gap-2 px-10 py-4 border-2 border-accent-foreground text-accent-foreground font-semibold text-sm uppercase tracking-wider hover:bg-accent-foreground hover:text-accent transition-all">
                <Phone className="w-4 h-4" /> {c.buttonText2 || "Call Now"}{formattedPhone ? `: ${formattedPhone}` : ''}
              </a>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ─── SHARED PARTNERS ─── Synced with admin panel
export function SharedPartners() {
  const { getPublishedPageContent } = useTheme();
  const content = getPublishedPageContent('home', 'partners');
  const c = {
    heading: "Trusted Partners",
    ...content,
  };

  // Read partners from admin localStorage
  const partners = useMemo(() => {
    try {
      const stored = localStorage.getItem('developer_partners');
      if (stored) {
        const all = JSON.parse(stored);
        const active = all.filter((p: any) => p.is_active);
        if (active.length > 0) return active.map((p: any) => ({ name: p.name, logo: p.logo_url }));
      }
    } catch {}
    return [
      { name: "DLF" }, { name: "Godrej" }, { name: "M3M" }, { name: "Sobha" },
      { name: "Prestige" }, { name: "Tata" }, { name: "Lodha" }, { name: "Mahindra" },
    ];
  }, []);

  const doubled = [...partners, ...partners];

  return (
    <section className="py-12 bg-secondary border-y border-border overflow-hidden">
      <div className="text-center mb-8">
        <span className="text-muted-foreground uppercase tracking-widest text-xs font-semibold">{c.heading}</span>
      </div>
      <div className="relative">
        <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-secondary to-transparent z-10" />
        <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-secondary to-transparent z-10" />
        <motion.div className="flex items-center gap-16" animate={{ x: [0, -50 * partners.length * 4] }}
          transition={{ x: { repeat: Infinity, repeatType: "loop", duration: partners.length * 4, ease: "linear" } }}>
          {doubled.map((p: any, i: number) => (
            p.logo ? (
              <img key={i} src={p.logo} alt={p.name} className="h-10 w-auto object-contain flex-shrink-0 min-w-[120px] opacity-50 hover:opacity-100 transition-opacity grayscale hover:grayscale-0" />
            ) : (
              <span key={i} className="text-muted-foreground/50 hover:text-foreground transition-colors font-serif text-xl font-semibold whitespace-nowrap flex-shrink-0 min-w-[120px]">{p.name}</span>
            )
          ))}
        </motion.div>
      </div>
    </section>
  );
}
